import { AxiosError } from 'axios';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { getProtectedAxiosInstance } from '../axios/Axios';

/**
 * Turns whatever the checkout endpoint threw into something worth showing a
 * user. Handles the one response that is not JSON: the shared session
 * middleware answers 401 with text/plain, so the usual
 * `{ error: { message } }` body is not there to read.
 */
export function getLifetimeCheckoutErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    if (
      error?.response?.status === 401 &&
      typeof error?.response?.data === 'string'
    ) {
      return 'Your session has expired. Please refresh the page and try again.';
    }

    const message = error?.response?.data?.error?.message;
    if (message) {
      return message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
}

/**
 * Creates a one-time Stripe Checkout session for a lifetime plan and resolves
 * with the URL to send the user to.
 *
 * successUrl/cancelUrl are deliberately omitted - the API only accepts
 * redirects on its own APP_URL origin and otherwise silently substitutes its
 * defaults, which land on the plan and billing page either way.
 */
export async function createLifetimeCheckoutUrl(
  orgId: string,
  planId: string
): Promise<string> {
  const protectedAxiosInstance = getProtectedAxiosInstance();

  const result = await protectedAxiosInstance.post(
    '/stripe/create-lifetime-checkout',
    { orgId, planId }
  );

  const checkoutUrl = result?.data?.data?.checkoutUrl as string | undefined;

  if (!checkoutUrl) {
    throw new Error('We could not start the checkout. Please try again.');
  }

  return checkoutUrl;
}

/**
 * Clears a checkout loading flag when the page comes back from the browser's
 * back/forward cache. Claiming navigates away with the spinner still on, so a
 * user who backs out of Stripe Checkout is handed the exact DOM they left -
 * spinner included - and without this the CTA loads forever. A full reload
 * resets state on its own; only the bfcache restore needs handling.
 */
export function useResetLoadingOnBackNavigation(
  setIsLoading: Dispatch<SetStateAction<boolean>>
): void {
  useEffect(() => {
    function handlePageshow(event: PageTransitionEvent) {
      if (event.persisted) {
        setIsLoading(false);
      }
    }

    window.addEventListener('pageshow', handlePageshow);

    return () => window.removeEventListener('pageshow', handlePageshow);
  }, [setIsLoading]);
}
